import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {FocusKeys} from '@primer/behaviors'
import {AnchoredOverlay, Button, PortalContext, useFocusZone} from '@primer/react'
import {lazy, memo, startTransition, Suspense, use, useCallback, useId, useState} from 'react'

import {ReactionButton} from './components/ReactionButton'
import {ReactionViewerAnchor} from './components/ReactionViewerAnchor'
import type {ReactionContent, ReactionViewerGroup} from './utils/ReactionGroups'

const ReactionPicker = lazy(() => import('./components/ReactionPicker'))

function ReactionPickerLoadError() {
  const errorId = useId()

  return (
    <div className="p-3">
      <p id={errorId}>The reaction picker failed to load.</p>
      <Button aria-describedby={errorId} onClick={() => window.location.reload()}>
        Reload page
      </Button>
    </div>
  )
}

type ReactionViewerBaseProps = {
  reactionContext?: string
  reactionGroups: ReactionViewerGroup[]
  canReact?: boolean
  showReactors?: boolean
  onReact: (reaction: ReactionContent, viewerHasReacted: boolean) => void
  ref?: React.Ref<HTMLButtonElement>
}

export const ReactionViewerBase = memo(function ReactionViewerBaseComponent({
  reactionContext,
  ref,
  reactionGroups,
  canReact = true,
  showReactors = false,
  onReact,
}: ReactionViewerBaseProps) {
  const [isOpen, setIsOpen] = useState(false)
  const {portalContainerName} = use(PortalContext)
  const reactionsAvailable = reactionGroups.length > 0
  const handleMenuReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      onReact(reaction, viewerHasReacted)
      setIsOpen(false)
    },
    [onReact],
  )

  const {containerRef} = useFocusZone({
    bindKeys: FocusKeys.HomeAndEnd | FocusKeys.ArrowHorizontal,
    focusOutBehavior: 'wrap',
  })

  return (
    <div
      role="toolbar"
      aria-label={reactionContext ? `Reactions for ${reactionContext}` : 'Reactions'}
      ref={containerRef as React.RefObject<HTMLDivElement | null>}
      className="d-flex gap-1 flex-wrap"
    >
      {canReact && (
        <Suspense fallback={null}>
          <AnchoredOverlay
            open={isOpen}
            onOpen={() => {
              if (reactionsAvailable) startTransition(() => setIsOpen(true))
            }}
            onClose={() => setIsOpen(false)}
            // Explicit scoped portals let Primer avoid CSS anchors inside transformed scroll containers.
            overlayProps={{portalContainerName}}
            focusZoneSettings={{
              bindKeys: FocusKeys.ArrowAll | FocusKeys.HomeAndEnd,
              focusOutBehavior: 'wrap',
            }}
            renderAnchor={p => (
              <ReactionViewerAnchor
                renderAnchorProps={p}
                reactionsAvailable={reactionsAvailable}
                reactionContext={reactionContext}
                ref={ref}
              />
            )}
          >
            {isOpen ? (
              <ErrorBoundary boundaryName="ReactionPicker" fallback={<ReactionPickerLoadError />}>
                <ReactionPicker reactionGroups={reactionGroups} onReact={handleMenuReact} />
              </ErrorBoundary>
            ) : null}
          </AnchoredOverlay>
        </Suspense>
      )}
      {(reactionGroups || []).map((reactionGroup, index) => (
        <ReactionButton
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          reactionGroup={reactionGroup}
          disabled={!canReact}
          showReactors={showReactors}
          onReact={onReact}
        />
      ))}
    </div>
  )
})

ReactionViewerBase.displayName = 'ReactionViewerBase'
